import { Form } from 'antd';
import { useCallback, useMemo } from 'react';

import type { PhoneState } from '@app/components/Phone/types';
import { getPhoneErrorMessage } from '@app/components/Phone/utils/getPhoneErrorMessage';
import { selectCountries, useAppSelector } from '@app/store';

import type { Rule } from 'antd/es/form';

interface UsePhoneCodeFormProps {
  onSubmitPhone(phone: string): void;
}

interface PhoneFormValues {
  phone?: PhoneState;
}

const REQUIRED_PHONE_MESSAGE = 'Укажите номер телефона';

export const usePhoneCodeForm = ({ onSubmitPhone }: UsePhoneCodeFormProps) => {
  const [form] = Form.useForm<PhoneFormValues>();
  const countries = useAppSelector(selectCountries);

  const getCurrentPhoneError = useCallback(
    (value: unknown): string | null => {
      if (
        typeof value !== 'object' ||
        value === null ||
        !('Phone' in value) ||
        typeof value.Phone !== 'string' ||
        !/^\d+$/.test(value.Phone) ||
        !('CountryId' in value)
      ) {
        return REQUIRED_PHONE_MESSAGE;
      }

      const phone = value.Phone;
      const countryId = value.CountryId;
      const country = countries.find(({ Id }) => Id === countryId);
      if (
        !country?.TelCode ||
        !country.MinDigits ||
        !country.MaxDigits ||
        !phone.startsWith(country.TelCode)
      ) {
        return REQUIRED_PHONE_MESSAGE;
      }

      const validation = getPhoneErrorMessage({
        maskedPhone: phone,
        telCode: country.TelCode,
        minDigits: country.MinDigits,
        maxDigits: country.MaxDigits,
        required: true,
        isEdited: true,
      });

      return validation.isValid
        ? null
        : validation.errorMessage || REQUIRED_PHONE_MESSAGE;
    },
    [countries]
  );

  const submitPhoneHandler = useCallback(
    (values: PhoneFormValues) => {
      const { phone } = values;
      if (phone && !getCurrentPhoneError(phone)) {
        onSubmitPhone(phone.Phone);
      }
    },
    [getCurrentPhoneError, onSubmitPhone]
  );

  const phoneRules = useMemo<Rule[]>(
    () => [
      { required: true, message: REQUIRED_PHONE_MESSAGE },
      {
        validator: (_rule, value: unknown) => {
          const errorMessage = getCurrentPhoneError(value);
          return errorMessage
            ? Promise.reject(new Error(errorMessage))
            : Promise.resolve();
        },
      },
    ],
    [getCurrentPhoneError]
  );

  return { form, phoneRules, submitPhoneHandler };
};
